<template>
    <div class="px-4 sm:px-6 lg:px-8 py-8">
        <div class="sm:flex sm:items-center sm:justify-between">
            <div class="sm:flex-auto">
                <h1 class="text-2xl font-semibold text-gray-900">Analytika</h1>
                <p class="mt-2 text-sm text-gray-700">Prehľad generovaných dokladov a štatistík</p>
            </div>
        </div>

        <!-- Date Filter -->
        <div class="mt-6 bg-white shadow rounded-lg p-6">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-5">
                <div>
                    <label for="start-date" class="block text-sm font-medium text-gray-700">Od dátumu</label>
                    <input type="date" id="start-date" v-model="filters.start_date"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                    <label for="end-date" class="block text-sm font-medium text-gray-700">Do dátumu</label>
                    <input type="date" id="end-date" v-model="filters.end_date"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                    <label for="document-type" class="block text-sm font-medium text-gray-700">Typ dokladu</label>
                    <select id="document-type" v-model="filters.document_type"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        <option value="">Všetky</option>
                        <option v-for="type in documentTypes" :key="type.id" :value="type.id">
                            {{ type.name }}{{ type.description ? ' - ' + type.description : '' }}
                        </option>
                    </select>
                </div>
                <div>
                    <label for="document-subtype" class="block text-sm font-medium text-gray-700">Podtyp dokladu</label>
                    <select id="document-subtype" v-model="filters.document_subtype"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        <option value="">Všetky</option>
                        <option v-for="subtype in documentSubtypes" :key="subtype.id" :value="subtype.id">
                            {{ subtype.name }} - {{ subtype.description }}
                        </option>
                    </select>
                </div>
                <div class="flex items-end gap-2">
                    <button @click="applyFilters" type="button"
                        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Filtrovať
                    </button>
                    <button @click="clearFilters" type="button"
                        class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Vymazať
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="mt-8 flex justify-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>

        <!-- Content -->
        <div v-else>
            <!-- Monthly Comparison Cards -->
            <div class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
                <div class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">Počet dokladov za aktuálny
                                        mesiac</dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ currentMonthDocuments }}</dd>
                                    <dd class="text-xs text-gray-500 mt-1">{{ getCurrentMonthName() }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">Počet dokladov za
                                        predchádzajúci mesiac</dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ previousMonthDocuments }}</dd>
                                    <dd class="text-xs text-gray-500 mt-1">{{ getPreviousMonthName() }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <div class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">Celkový počet dokladov</dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ totalDocuments }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">Počet spoločností</dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ totalCompanies }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">Priemerný počet dokladov na
                                        spoločnosť
                                    </dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ averageDocsPerCompany }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Documents by Company Table -->
            <div class="mt-8 bg-white shadow rounded-lg overflow-hidden">
                <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h2 class="text-lg font-medium text-gray-900">Doklady podľa spoločností</h2>
                </div>
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Spoločnosť
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    IČO
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Počet dokladov
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Posledný doklad
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Akcie
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="company in documentsByCompany" :key="company.id">
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {{ company.company_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ company.ico }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ company.documents_count }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ company.last_document_date ? formatDate(company.last_document_date) : 'N/A' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <router-link :to="{ name: 'Detail spoločnosti', params: { id: company.id } }"
                                        class="text-indigo-600 hover:text-indigo-900">
                                        Detail spoločnosti
                                    </router-link>
                                </td>
                            </tr>
                            <tr v-if="documentsByCompany.length === 0">
                                <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                                    Žiadne doklady neboli nájdené
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="pagination.total > pagination.per_page"
                    class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div class="flex-1 flex justify-between sm:hidden">
                        <button @click="changePage(pagination.current_page - 1)"
                            :disabled="pagination.current_page === 1"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                            Predchádzajúca
                        </button>
                        <button @click="changePage(pagination.current_page + 1)"
                            :disabled="pagination.current_page === pagination.last_page"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                            Nasledujúca
                        </button>
                    </div>
                    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p class="text-sm text-gray-700">
                                Zobrazujem <span class="font-medium">{{ pagination.from }}</span> až
                                <span class="font-medium">{{ pagination.to }}</span> z
                                <span class="font-medium">{{ pagination.total }}</span> výsledkov
                            </p>
                        </div>
                        <div>
                            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                aria-label="Pagination">
                                <button @click="changePage(pagination.current_page - 1)"
                                    :disabled="pagination.current_page === 1"
                                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <span class="sr-only">Predchádzajúca</span>
                                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clip-rule="evenodd" />
                                    </svg>
                                </button>
                                <button @click="changePage(pagination.current_page + 1)"
                                    :disabled="pagination.current_page === pagination.last_page"
                                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <span class="sr-only">Nasledujúca</span>
                                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clip-rule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import AnalyticsDataService from '@/services/AnalyticsDataService'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface CompanyDocumentData {
    id: number
    company_name: string
    ico: string
    documents_count: number
    last_document_date: string | null
}

interface DocumentTypeData {
    type_id: number
    type_name: string
    count: number
}

interface DocumentType {
    id: number
    name: string
    description?: string
}

interface DocumentSubtype {
    id: number
    name: string
    description: string
}

export default defineComponent({
    name: 'AnalyticsView',
    components: { Bar },
    data() {
        return {
            loading: true,
            totalDocuments: 0,
            totalCompanies: 0,
            averageDocsPerCompany: '0',
            currentMonthDocuments: 0,
            previousMonthDocuments: 0,
            filters: {
                start_date: '',
                end_date: '',
                document_type: '',
                document_subtype: ''
            },
            documentsByCompany: [] as CompanyDocumentData[],
            documentTypes: [] as DocumentType[],
            documentSubtypes: [] as DocumentSubtype[],
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
                from: 0,
                to: 0
            },
            documentTypeData: {
                labels: [] as string[],
                datasets: [
                    {
                        label: 'Počet dokladov',
                        backgroundColor: '#3b82f6',
                        data: [] as number[]
                    }
                ]
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        }
                    }
                }
            }
        }
    },
    mounted() {
        this.loadDocumentTypes()
        this.loadDocumentSubtypes()
        this.loadAnalytics()
    },
    methods: {
        async loadDocumentTypes() {
            try {
                const response = await AnalyticsDataService.getDocumentTypes()
                if (response.data.success) {
                    this.documentTypes = response.data.data
                }
            } catch (error) {
                console.error('Error loading document types:', error)
            }
        },
        async loadDocumentSubtypes() {
            try {
                const response = await AnalyticsDataService.getDocumentSubtypes()
                if (response.data.success) {
                    this.documentSubtypes = response.data.data
                }
            } catch (error) {
                console.error('Error loading document subtypes:', error)
            }
        },
        async loadAnalytics() {
            this.loading = true
            try {
                // Load overall statistics
                const dateFilters = this.filters.start_date || this.filters.end_date || this.filters.document_type || this.filters.document_subtype ? this.filters : undefined
                const analyticsResponse = await AnalyticsDataService.getDocumentAnalytics(dateFilters)
                if (analyticsResponse.data.success) {
                    this.totalDocuments = analyticsResponse.data.data.total_documents || 0
                    this.totalCompanies = analyticsResponse.data.data.total_companies || 0
                    this.currentMonthDocuments = analyticsResponse.data.data.current_month_documents || 0
                    this.previousMonthDocuments = analyticsResponse.data.data.previous_month_documents || 0
                    this.averageDocsPerCompany =
                        this.totalCompanies > 0
                            ? String((this.totalDocuments / this.totalCompanies).toFixed(2))
                            : '0'
                }                // Load documents by company
                await this.loadDocumentsByCompany(1)

                // Load document type distribution
                const typeResponse = await AnalyticsDataService.getDocumentTypeDistribution(dateFilters)
                if (typeResponse.data.success && typeResponse.data.data.length > 0) {
                    this.documentTypeData.labels = typeResponse.data.data.map((item: DocumentTypeData) => item.type_name || 'Neznámy')
                    this.documentTypeData.datasets[0].data = typeResponse.data.data.map((item: DocumentTypeData) => item.count)
                }
            } catch (error) {
                console.error('Error loading analytics:', error)
                toast.error('Chyba pri načítavaní analytických údajov')
            } finally {
                this.loading = false
            }
        },
        async loadDocumentsByCompany(page: number) {
            try {
                const dateFilters = this.filters.start_date || this.filters.end_date || this.filters.document_type || this.filters.document_subtype ? this.filters : undefined
                const response = await AnalyticsDataService.getDocumentsByCompany(page, dateFilters)
                if (response.data.success) {
                    this.documentsByCompany = response.data.data.data || []
                    this.pagination = {
                        current_page: response.data.data.current_page || 1,
                        last_page: response.data.data.last_page || 1,
                        per_page: response.data.data.per_page || 10,
                        total: response.data.data.total || 0,
                        from: response.data.data.from || 0,
                        to: response.data.data.to || 0
                    }
                }
            } catch (error) {
                console.error('Error loading documents by company:', error)
                toast.error('Chyba pri načítavaní dokladov podľa spoločností')
            }
        },
        changePage(page: number) {
            if (page >= 1 && page <= this.pagination.last_page) {
                this.loadDocumentsByCompany(page)
            }
        },
        formatDate(dateString: string) {
            if (!dateString) return 'N/A'
            const date = new Date(dateString)
            return date.toLocaleDateString('sk-SK', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            })
        },
        applyFilters() {
            this.loadAnalytics()
        },
        clearFilters() {
            this.filters.start_date = ''
            this.filters.end_date = ''
            this.filters.document_type = ''
            this.filters.document_subtype = ''
            this.loadAnalytics()
        },
        getCurrentMonthName() {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            return months[new Date().getMonth()]
        },
        getPreviousMonthName() {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            const prevMonth = new Date().getMonth() - 1
            return months[prevMonth < 0 ? 11 : prevMonth]
        }
    }
})
</script>
